import Add16 from '@carbon/icons-react/es/add/16';
import { Upload } from 'antd';
import { RcFile } from 'antd/es/upload/interface';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  FileAttachment,
  FileUploadStatus,
  getUpdatedFileList,
  UploadFileWithAttachment,
} from 'api/attachments';
import { TaskAPI } from 'api/tasks';
import { AttachmentGallery } from 'components/atoms/attachment-gallery';
import { Button } from 'components/atoms/button';
import { toastService } from 'components/molecules/toast';
import { HelperText01, Text03 } from 'components/styles';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { handleDownload } from 'utils/attachment';
import { uid } from 'utils/uid';
import { Empty } from '../empty';
import { AttachmentsTable } from './attachments-table';
import styles from './attachments.module.css';
import { AttachmentsTabViews } from './types';

const { Dragger } = Upload;

const AttachmentsEmpty = styled(Empty)`
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  padding-left: var(--spacing-03);
`;

const convertFileAttachmentToUploadFileWithAttachment = (
  data: FileAttachment[],
): UploadFileWithAttachment[] =>
  data.map((d) => ({
    ...d,
    uid: uid(String(d.attachmentId)),
    size: d.sizeInBytes,
    type: 'TYPE',
    attachment: d,
    status: FileUploadStatus.Done,
    thumbUrl: d?.thumbLocation || d?.location,
    url: d?.location,
  }));

const AddButton = () => (
  <StyledButton
    data-cy="attachments.add"
    type="link"
    size="small"
    icon={<Add16 />}
  >
    <FormattedMessage id="attachment.add" />
  </StyledButton>
);

const Description = styled.div`
  ${HelperText01};
  ${Text03};
`;

export const TaskAttachmentsList: FC<{
  view?: AttachmentsTabViews;
  data?: FileAttachment[];
  onChange?(filesList: UploadFileWithAttachment[]): void;
  onUploadRequest(file: File): Promise<any>;
  onMount?: (ref) => void;
}> = ({ view, data = [], onChange = () => {}, onUploadRequest, onMount }) => {
  const [filesList, setFilesList] = useState<UploadFileWithAttachment[]>([]);
  const latestFileList = useRef<UploadFileWithAttachment[]>(filesList);
  const stringifyData = JSON.stringify(data);
  const uploadButtonRef = useRef<any>(null);
  const downloadAttachmentErrorText = useTranslation(
    'attachments.download.error',
  );
  const translate = useTranslate();

  useEffect(() => {
    setFilesList(
      convertFileAttachmentToUploadFileWithAttachment(
        JSON.parse(stringifyData),
      ),
    );
  }, [stringifyData]);

  useEffect(() => {
    onMount?.(uploadButtonRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadButtonRef]);
  useEffect(() => {
    latestFileList.current = filesList;
  }, [filesList]);

  const updateFile = ({
    attachment,
    file,
    status,
  }: {
    attachment?: FileAttachment;
    file: RcFile;
    status: FileUploadStatus;
  }) => {
    const latestFiles = latestFileList.current;
    const fileToUpdate = latestFiles.find((f) => f.uid === file.uid);

    if (!fileToUpdate) {
      return;
    }

    const result = getUpdatedFileList(
      fileToUpdate,
      latestFiles,
      attachment,
      status,
    );

    if (result) {
      setFilesList(result);

      onChange(result);
    }
  };

  const customUploadRequest = (args) => {
    const { file } = args;

    onUploadRequest(file)
      .then((res) => {
        updateFile({
          attachment: res,
          file,
          status: FileUploadStatus.Done,
        });
      })
      .catch(({ response = {} }) => {
        const errorData = response.data || {};
        const [error = {}] = errorData.errors || [];

        if (error.code === 'ATTACHMENT_SIZE_EXCEEDED') {
          toastService({
            title: translate('attachment.error.size.title'),
            message: translate('attachment.add.description'),
          }).error();
        }
        updateFile({
          attachment: undefined,
          file,
          status: FileUploadStatus.Error,
        });
      });
  };

  const handleOnRemove = (file: UploadFileWithAttachment) => {
    const index = filesList.indexOf(file);
    const newFileList = [...filesList];

    newFileList.splice(index, 1);

    setFilesList(newFileList);
    onChange(newFileList);

    if (file.attachment?.attachmentId) {
      TaskAPI.deleteTaskAttachment(file.attachment.attachmentId);
    }

    return true;
  };

  let node;

  switch (view) {
    case AttachmentsTabViews.GridView:
      node = (
        <AttachmentGallery
          large
          className={styles.gallery}
          filesList={filesList}
          onRemoveFile={handleOnRemove}
          onDownloadFile={(file) =>
            handleDownload(
              file.attachment?.attachmentId,
              downloadAttachmentErrorText,
            )
          }
        />
      );
      break;
    case AttachmentsTabViews.TableView:
    default:
      node = (
        <AttachmentsTable
          filesList={filesList}
          onRemoveFile={handleOnRemove}
          onDownloadFile={(file) =>
            handleDownload(
              file.attachment?.attachmentId,
              downloadAttachmentErrorText,
            )
          }
        />
      );
  }

  const handleOnChange = (args) => {
    const { file } = args;

    setFilesList([...latestFileList.current, file]);
  };

  return (
    /* eslint-disable react/jsx-props-no-spreading */
    <>
      <Dragger
        className={styles.dragger}
        customRequest={customUploadRequest}
        onChange={handleOnChange}
        multiple
        showUploadList={false}
        openFileDialogOnClick={!filesList.length}
      >
        <div className={styles.content_wrapper} ref={uploadButtonRef}>
          {filesList.length ? (
            node
          ) : (
            <AttachmentsEmpty>
              {/* Note:: Add button is dummy here, if user clicks on it,
              the event will propagate to dragger and open file manager. */}
              <AddButton />
              <Description>
                <FormattedMessage id="attachment.add.description" />
              </Description>
              {/* <NoContentSvg /> */}
            </AttachmentsEmpty>
          )}
        </div>
      </Dragger>
      {filesList.length > 0 && (
        <Upload
          customRequest={customUploadRequest}
          onChange={handleOnChange}
          multiple
          showUploadList={false}
        >
          <AddButton />
        </Upload>
      )}
    </>
    /* eslint-enable react/jsx-props-no-spreading */
  );
};
